// FantasyData API Service via GraphQL Lambda Proxy
// Documentation: https://fantasydata.com/products/mlb-api

import { generateClient } from 'aws-amplify/api';

/**
 * Master switch for the entire FantasyData / SportsData.io integration.
 *
 * OFF for now - we're building the UI against fake/sample data and will wire the
 * real APIs up later. (The FANTASY_DATA_API_KEY secret on the fantasy-data-api
 * Lambda is also currently invalid / over quota, so live calls only 401.)
 *
 * Every FantasyData request flows through fetchFromAPI() below, which
 * short-circuits when this is false so NO network calls are made - each caller
 * (games ticker, player headshots, news, season stats, profiles) falls back to
 * its built-in sample/empty data. Flip to `true` once a valid key is configured
 * and we're ready to integrate for real.
 */
export const FANTASY_DATA_ENABLED = false;

// GraphQL query for fetching fantasy data
const fetchFantasyDataQuery = /* GraphQL */ `
  query FetchFantasyData($endpoint: String!) {
    fetchFantasyData(endpoint: $endpoint)
  }
`;

// Helper function to make API requests through GraphQL Lambda proxy
const fetchFromAPI = async (endpoint) => {
  // Integration disabled - fail fast and quietly so callers use fake/fallback
  // data without spamming the console or hitting the (currently dead) API.
  if (!FANTASY_DATA_ENABLED) {
    throw new Error('FantasyData integration is disabled (FANTASY_DATA_ENABLED is false).');
  }
  try {
    const client = generateClient();
    
    // Call the GraphQL query
    const response = await client.graphql({
      query: fetchFantasyDataQuery,
      variables: { endpoint },
      authMode: 'userPool'
    });

    // Check for GraphQL errors
    if (response.errors && response.errors.length > 0) {
      const error = response.errors[0];
      console.error('❌ GraphQL Error Message:', error.message);
      console.error('❌ Error Details:', JSON.stringify(error, null, 2));
      throw new Error(`GraphQL Error: ${error.message}`);
    }

    const data = response.data.fetchFantasyData;

    if (!data) {
      throw new Error('No data received from proxy');
    }

    // Parse the JSON response if it's a string
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch (error) {
    // Log the full error details
    if (error.errors && error.errors.length > 0) {
      const errorMessage = error.errors[0].message;
      console.error('❌ Lambda Error:', errorMessage);
      console.error('❌ Full Error Object:', JSON.stringify(error.errors[0], null, 2));
      
      // Check for quota/rate limit errors
      if (errorMessage.includes('403') || errorMessage.includes('429') || 
          errorMessage.includes('quota') || errorMessage.includes('rate limit')) {
        console.warn('⚠️ FantasyData API quota exceeded - API calls disabled');
        throw new Error('FantasyData API quota exceeded. Please try again later or upgrade your API plan.');
      }
    } else {
      const errorMessage = error.message || error.toString();
      console.error('❌ FantasyData Proxy Error:', errorMessage);
      
      // Check for quota/rate limit errors
      if (errorMessage.includes('403') || errorMessage.includes('429') || 
          errorMessage.includes('quota') || errorMessage.includes('rate limit')) {
        throw new Error('FantasyData API quota exceeded. Please try again later or upgrade your API plan.');
      }
    }
    throw error;
  }
};

// Get all active MLB players
export const getActivePlayers = async () => {
  return fetchFromAPI('/scores/json/Players');
};

// Get players by team
export const getPlayersByTeam = async (teamAbbreviation) => {
  return fetchFromAPI(`/scores/json/Players/${teamAbbreviation}`);
};

// Get current season schedule
export const getCurrentSeasonSchedule = async (season = '2026') => {
  return fetchFromAPI(`/scores/json/Games/${season}`);
};

// Get games for a specific date (format: YYYY-MMM-DD, e.g., 2025-APR-15)
export const getGamesByDate = async (date) => {
  return fetchFromAPI(`/scores/json/GamesByDate/${date}`);
};

// Get current season standings
export const getStandings = async (season = '2026') => {
  return fetchFromAPI(`/scores/json/Standings/${season}`);
};

// Get player season stats
export const getPlayerSeasonStats = async (season = '2026') => {
  return fetchFromAPI(`/stats/json/PlayerSeasonStats/${season}`);
};

// Get player game stats by date
export const getPlayerGameStatsByDate = async (date) => {
  return fetchFromAPI(`/stats/json/PlayerGameStatsByDate/${date}`);
};

// Get player stats for last N days
export const getPlayerStatsForPeriod = async (days) => {
  try {
    const stats = {};
    const today = new Date();
    
    // Get stats for each day in the period
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      const year = date.getFullYear();
      const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
      const day = String(date.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      try {
        const dayStats = await getPlayerGameStatsByDate(formattedDate);
        
        // Aggregate stats by player
        dayStats.forEach(stat => {
          if (!stats[stat.PlayerID]) {
            stats[stat.PlayerID] = {
              PlayerID: stat.PlayerID,
              Name: stat.Name,
              Team: stat.Team,
              Position: stat.Position,
              // Batting stats
              AtBats: 0,
              Hits: 0,
              HomeRuns: 0,
              RunsBattedIn: 0,
              Runs: 0,
              StolenBases: 0,
              Walks: 0,
              Strikeouts: 0,
              // Pitching stats
              InningsPitchedDecimal: 0,
              EarnedRunsAllowed: 0,
              HitsAllowed: 0,
              WalksAllowed: 0,
              PitcherStrikeouts: 0,
              Wins: 0,
              Losses: 0,
              Saves: 0,
              Games: 0
            };
          }
          
          const playerStats = stats[stat.PlayerID];
          
          // Aggregate batting stats
          playerStats.AtBats += stat.AtBats || 0;
          playerStats.Hits += stat.Hits || 0;
          playerStats.HomeRuns += stat.HomeRuns || 0;
          playerStats.RunsBattedIn += stat.RunsBattedIn || 0;
          playerStats.Runs += stat.Runs || 0;
          playerStats.StolenBases += stat.StolenBases || 0;
          playerStats.Walks += stat.Walks || 0;
          playerStats.Strikeouts += stat.Strikeouts || 0;
          
          // Aggregate pitching stats
          playerStats.InningsPitchedDecimal += stat.InningsPitchedDecimal || 0;
          playerStats.EarnedRunsAllowed += stat.EarnedRunsAllowed || 0;
          playerStats.HitsAllowed += stat.HitsAllowed || 0;
          playerStats.WalksAllowed += stat.WalksAllowed || 0;
          playerStats.PitcherStrikeouts += stat.PitcherStrikeouts || 0;
          playerStats.Wins += stat.Wins || 0;
          playerStats.Losses += stat.Losses || 0;
          playerStats.Saves += stat.Saves || 0;
          playerStats.Games += 1;
        });
      } catch (error) {
        console.warn(`No stats for date ${formattedDate}:`, error.message);
      }
    }
    
    // Calculate derived stats (averages, ERA, WHIP)
    return Object.values(stats).map(player => {
      const battingAverage = player.AtBats > 0 ? player.Hits / player.AtBats : 0;
      const era = player.InningsPitchedDecimal > 0 
        ? (player.EarnedRunsAllowed * 9) / player.InningsPitchedDecimal 
        : 0;
      const whip = player.InningsPitchedDecimal > 0 
        ? (player.HitsAllowed + player.WalksAllowed) / player.InningsPitchedDecimal 
        : 0;
      
      return {
        ...player,
        BattingAverage: battingAverage,
        EarnedRunAverage: era,
        WalksHitsPerInningsPitched: whip,
        Strikeouts: player.PitcherStrikeouts || player.Strikeouts
      };
    });
  } catch (error) {
    console.error('Error fetching period stats:', error);
    return [];
  }
};

// Get team season stats
export const getTeamSeasonStats = async (season = '2026') => {
  return fetchFromAPI(`/stats/json/TeamSeasonStats/${season}`);
};

// Get current day's games (today)
export const getTodaysGames = async () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  const day = String(today.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;
  
  return getGamesByDate(formattedDate);
};

// Get player by ID
export const getPlayerDetails = async (playerId) => {
  return fetchFromAPI(`/scores/json/Player/${playerId}`);
};

// Get news (league-wide)
export const getNews = async () => {
  return fetchFromAPI('/scores/json/News');
};

// Get news by player
export const getNewsByPlayer = async (playerId) => {
  return fetchFromAPI(`/scores/json/NewsByPlayerID/${playerId}`);
};

// Get projected player stats (for DFS)
export const getProjectedPlayerStats = async (date) => {
  return fetchFromAPI(`/projections/json/PlayerGameProjectionStatsByDate/${date}`);
};

// Get stadium info
export const getStadiums = async () => {
  return fetchFromAPI('/scores/json/Stadiums');
};

// Get teams
export const getTeams = async () => {
  return fetchFromAPI('/scores/json/AllTeams');
};

// Get injuries
export const getInjuries = async () => {
  return fetchFromAPI('/scores/json/Injuries');
};

// Get all player headshot URLs
export const getHeadshots = async () => {
  return fetchFromAPI('/headshots/json/Headshots');
};

// Search for players by name
export const searchPlayers = async (searchTerm) => {
  try {
    const allPlayers = await getActivePlayers();
    const term = searchTerm.toLowerCase();
    
    return allPlayers.filter(player => 
      player.FirstName?.toLowerCase().includes(term) ||
      player.LastName?.toLowerCase().includes(term) ||
      `${player.FirstName} ${player.LastName}`.toLowerCase().includes(term)
    );
  } catch (error) {
    console.error('Error searching players:', error);
    return [];
  }
};

// Map Fantasy Data API stats to our app's shorthand format
export const mapStatsFromAPI = (apiStats) => {
  return {
    // Batting stats
    avg: apiStats.BattingAverage || 0,              // AVG = BattingAverage
    hr: apiStats.HomeRuns || 0,                     // HR = HomeRuns
    rbi: apiStats.RunsBattedIn || 0,                // RBI = RunsBattedIn
    r: apiStats.Runs || 0,                          // R = Runs
    sb: apiStats.StolenBases || 0,                  // SB = StolenBases
    // Pitching stats
    era: apiStats.EarnedRunAverage || 0,            // ERA = EarnedRunAverage
    whip: apiStats.WalksHitsPerInningsPitched || 0, // WHIP = WalksHitsPerInningsPitched
    k: apiStats.Strikeouts || apiStats.PitcherStrikeouts || 0, // K = Strikeouts
    w: apiStats.Wins || 0,                          // W = Wins
    sv: apiStats.Saves || 0                         // SV = Saves
  };
};

// Format player data for our app
export const formatPlayerForApp = (apiPlayer) => {
  return {
    id: apiPlayer.PlayerID,
    name: `${apiPlayer.FirstName} ${apiPlayer.LastName}`,
    position: apiPlayer.Position,
    team: apiPlayer.Team,
    number: apiPlayer.Jersey,
    status: apiPlayer.Status,
    stats: mapStatsFromAPI(apiPlayer),  // Use the standardized mapping
    image: apiPlayer.PhotoUrl,
    birthDate: apiPlayer.BirthDate,
    height: apiPlayer.Height,
    weight: apiPlayer.Weight,
    experience: apiPlayer.Experience
  };
};

export default {
  getActivePlayers,
  getPlayersByTeam,
  getCurrentSeasonSchedule,
  getGamesByDate,
  getStandings,
  getPlayerSeasonStats,
  getPlayerGameStatsByDate,
  getPlayerStatsForPeriod,
  getTeamSeasonStats,
  getTodaysGames,
  getPlayerDetails,
  getNews,
  getNewsByPlayer,
  getProjectedPlayerStats,
  getStadiums,
  getTeams,
  getInjuries,
  searchPlayers,
  formatPlayerForApp,
  mapStatsFromAPI  // Export the stats mapping helper
};

